import { useState, useCallback } from 'react';
import { facturacionTecnicosAPI } from '../services/facturacionTecnicosAPI';
import { useAuth } from '../../../core/context/AuthContext';

export const useFacturacionTecnicos = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [turnos, setTurnos] = useState([]);
  const [turnosDetalle, setTurnosDetalle] = useState([]);
  const [notificaciones, setNotificaciones] = useState([]);
  const [estadoFacturacion, setEstadoFacturacion] = useState(null);
  const [totales, setTotales] = useState({
    totalPracticas: 0,
    totalPracticasPagas: 0,
    total: 0
  });

  const isAdmin = user?.role === 'Administrador' || user?.role === 'Administrativo' || 
                  user?.roles?.includes('ADMINISTRADOR') || user?.roles?.includes('ADMINISTRATIVO');

  const cargarFacturacion = useCallback(async (idTecnico, mes, anio) => {
    setLoading(true);
    try {
      const facturacion = await facturacionTecnicosAPI.getFacturacionCompletaTecnicos(idTecnico, mes, anio);
      setTurnos(facturacion || []);
    } catch (error) {
      console.error('Error al cargar facturación:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarDetalle = useCallback(async (idTecnico, mes, anio) => {
    setLoading(true);
    setTurnosDetalle([]);
    setTotales({ totalPracticas: 0, totalPracticasPagas: 0, total: 0 });
    try {
      const fechaDesde = `${anio}-${String(mes).padStart(2, '0')}-01`;
      const fechaHasta = mes === 12 
        ? `${anio + 1}-01-01` 
        : `${anio}-${String(mes + 1).padStart(2, '0')}-01`;

      const [detalle, estado] = await Promise.all([
        facturacionTecnicosAPI.getFacturacionTecnicos(idTecnico, fechaDesde, fechaHasta),
        facturacionTecnicosAPI.getEstadoFacturacion(idTecnico, fechaDesde, fechaHasta)
      ]);

      setTurnosDetalle(detalle || []);
      
      if (estado && estado.length > 0) {
        const ultimoEstado = estado.sort((a, b) => b.idEstadoFacturacionTecnico - a.idEstadoFacturacionTecnico)[0];
        setEstadoFacturacion(ultimoEstado);
      }

      calcularTotales(detalle || []);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      console.error('Error response:', error.response?.data);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarNotificaciones = useCallback(async (envia, recibe, mes, anio) => {
    try {
      const notifs = await facturacionTecnicosAPI.getNotificaciones(envia, recibe, mes, anio);
      setNotificaciones(notifs || []);
    } catch (error) {
      console.error('Error al cargar notificaciones:', error);
    }
  }, []);

  const calcularTotales = (data) => {
    if (!data || data.length === 0) {
      setTotales({ totalPracticas: 0, totalPracticasPagas: 0, total: 0 });
      return;
    }

    // Si es un array con un objeto de resumen (GetCompleteShiftBillingTechnicians)
    if (data[0]?.cantidad !== undefined && data[0]?.importe !== undefined) {
      setTotales({
        totalPracticas: data[0].cantidad || 0,
        totalPracticasPagas: data[0].cantidad || 0,
        total: parseFloat(data[0].importe || 0).toFixed(2)
      });
      return;
    }

    // Si es un array de turnos detallados
    let totalPracticas = 0;
    let totalPracticasPagas = 0;
    let total = 0;

    const estadosQueSuman = ['Realizado', 'Enviado', 'Escoreado / Muestras en carpeta', 'En proceso', 'Solicitado'];

    data.forEach(item => {
      const estado = item.estadosTurno?.estadoTurno || '';
      const importe = parseFloat(item.importe || 0);

      totalPracticas++;

      if (estadosQueSuman.includes(estado)) {
        totalPracticasPagas++;
        total += importe;
      }
    });

    setTotales({
      totalPracticas,
      totalPracticasPagas,
      total: total.toFixed(2)
    });
  };

  const subirFactura = useCallback(async (files, idEstadoFacturacion) => {
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append('files', file));
      formData.append('idEstadoFacturacion', String(idEstadoFacturacion));
      formData.append('usuario', user?.username || '');

      await facturacionTecnicosAPI.uploadFactura(formData);
      return true;
    } catch (error) {
      console.error('Error al subir factura:', error);
      throw error;
    }
  }, [user]);

  const subirComprobante = useCallback(async (files, idEstadoFacturacion) => {
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append('files', file));
      formData.append('idEstadoFacturacion', String(idEstadoFacturacion));
      formData.append('usuario', user?.username || '');

      if (!idEstadoFacturacion) {
        throw new Error('ID de estado de facturación no válido');
      }
      
      await facturacionTecnicosAPI.uploadComprobante(formData);
      return true;
    } catch (error) {
      console.error('Error al subir comprobante:', error);
      throw error;
    }
  }, [user]);

  const enviarNotificacion = useCallback(async (mensaje, mes, anio, destinatario) => {
    try {
      await facturacionTecnicosAPI.createNotificacion(destinatario, mes, anio, mensaje);
    } catch (error) {
      console.error('Error al enviar notificación:', error);
      throw error;
    }
  }, []);

  return {
    loading,
    turnos,
    turnosDetalle,
    setTurnosDetalle,
    notificaciones,
    estadoFacturacion,
    setEstadoFacturacion,
    totales,
    setTotales,
    isAdmin,
    cargarFacturacion,
    cargarDetalle,
    cargarNotificaciones,
    subirFactura,
    subirComprobante,
    enviarNotificacion
  };
};
